import { Errors } from "../../../../constants/errors";
import { contactApiService } from "../../../../cores/contacts/services/api";
import { ContactSource } from "../../../../cores/contacts/enum/ContactSource.enum";
import { ContactOwner } from "../../../../cores/contacts/interfaces/api/get-contact";
import { GetContactsParams } from "../../../../cores/contacts/interfaces/api/get-contacts";
import { FormLead } from "../../../../cores/facebookForm/interfaces/api/get-facebook-form";
import { PostContactRequest } from "../../../../cores/contacts/interfaces/api/post-contact";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\+?[\d\s().-]{7,}$/;

const looksLikePhone = (value: string) => {
  const trimmed = (value || "").trim();
  if (!trimmed || trimmed.includes("@")) return false;
  return PHONE_REGEX.test(trimmed);
};

const pickByQuestionKeys = (questions: string[], answers: string[], keys: string[]) => {
  const pairs = questions.map((q, i) => ({
    q: (q || "").toLowerCase(),
    a: String(answers[i] || "").trim(),
  }));
  return pairs.find((p) => keys.some((k) => p.q.includes(k)))?.a || "";
};

export const getContactApiErrorMessage = (error: unknown) => {
  const axiosError = error as {
    response?: { data?: { message?: string | string[] } };
    request?: { response?: string };
    message?: string;
  };
  const apiMessage = axiosError?.response?.data?.message;
  if (Array.isArray(apiMessage)) return apiMessage.join(", ");
  if (typeof apiMessage === "string" && apiMessage.trim()) return apiMessage;

  try {
    const rawResponse = axiosError?.request?.response;
    if (rawResponse) {
      const parsed = JSON.parse(rawResponse) as { message?: string | string[] };
      if (Array.isArray(parsed.message)) return parsed.message.join(", ");
      if (typeof parsed.message === "string" && parsed.message.trim()) {
        return parsed.message;
      }
    }
  } catch {
    // Ignore malformed API error payloads.
  }

  if (axiosError?.message) return axiosError.message;
  return "Error desconocido";
};

export const getContactApiErrorCode = (error: unknown) => {
  const axiosError = error as {
    response?: { data?: { code?: number } };
    request?: { response?: string };
  };

  if (axiosError?.response?.data?.code) {
    return axiosError.response.data.code;
  }

  try {
    const rawResponse = axiosError?.request?.response;
    if (rawResponse) {
      return (JSON.parse(rawResponse) as { code?: number }).code || 0;
    }
  } catch {
    // Ignore malformed API error payloads.
  }

  return 0;
};

export const isDuplicateContactError = (error: unknown) =>
  getContactApiErrorCode(error) === Errors.DuplicatedEmail ||
  getContactApiErrorMessage(error).toLowerCase().includes("duplicated");

export const isDuplicateOpportunityError = (error: unknown) =>
  getContactApiErrorCode(error) === Errors.DuplicatedData ||
  getContactApiErrorMessage(error).toLowerCase().includes("oportunidad");

const buildPhoneLookupVariants = (phone: string) => {
  const normalized = normalizeContactPhone(phone);
  if (!normalized) return [];

  const digits = normalized.replace(/\D/g, "");
  const variants = new Set<string>([normalized]);

  if (digits) variants.add(digits);
  if (normalized.startsWith("+")) variants.add(normalized.slice(1));
  if (digits.length >= 9) variants.add(digits.slice(-9));

  return Array.from(variants).filter((value) => value.length >= 5);
};

export const findExistingContactId = async (
  companyId: string,
  email: string,
  phone: string
) => {
  const lookupEmail = isValidContactEmail(email) ? email.trim().toLowerCase() : "";

  if (lookupEmail) {
    const byEmail = await contactApiService.getContacts(companyId, {
      page: 1,
      perPage: 1,
      email__p: lookupEmail,
    });
    const contactId = byEmail.contacts?.[0]?.id || "";
    if (contactId) return contactId;
  }

  const phoneVariants = buildPhoneLookupVariants(phone);
  for (const phoneVariant of phoneVariants) {
    const byPhone = await contactApiService.getContacts(companyId, {
      page: 1,
      perPage: 1,
      tel__p: phoneVariant,
    });
    const contactId = byPhone.contacts?.[0]?.id || "";
    if (contactId) return contactId;
  }

  return "";
};

export const isValidContactEmail = (email: string) =>
  !!email && EMAIL_REGEX.test(email) && email.length <= 55;

export const normalizeContactPhone = (phone: string) => {
  const trimmed = (phone || "").trim();
  if (!trimmed || trimmed.includes("@")) return "";
  return trimmed.length >= 5 && trimmed.length <= 35 ? trimmed : "";
};

export const extractLeadDataFromFormLead = (
  lead: FormLead,
  questions: string[] = []
): { name: string; email: string; phone: string } => {
  const answers = (lead.data || []).map((entry) => String(entry || "").trim());
  const namedFields = lead.fields || [];

  let name = "";
  let email = "";
  let phone = "";

  for (const field of namedFields) {
    const key = (field.name || "").toLowerCase();
    const value = (field.value || "").trim();
    if (!value) continue;

    if (!email && /mail|correo|e-mail/.test(key) && isValidContactEmail(value)) {
      email = value;
      continue;
    }
    if (!phone && /phone|tel|cel|whatsapp|movil|mobile|numero/.test(key)) {
      const normalized = normalizeContactPhone(value);
      if (normalized) phone = normalized;
      continue;
    }
    if (!name && /name|nombre/.test(key) && !isValidContactEmail(value) && !looksLikePhone(value)) {
      name = value;
    }
  }

  const qName = pickByQuestionKeys(questions, answers, [
    "full name",
    "first name",
    "name",
    "nombre",
  ]);
  const qEmail = pickByQuestionKeys(questions, answers, ["email", "correo", "e-mail"]);
  const qPhone = pickByQuestionKeys(questions, answers, [
    "phone",
    "tel",
    "cel",
    "whatsapp",
    "movil",
    "mobile",
    "numero",
  ]);

  if (!email && isValidContactEmail(qEmail)) email = qEmail;
  else if (!email && isValidContactEmail(qName)) email = qName;
  else if (!email && isValidContactEmail(qPhone)) email = qPhone;

  if (!phone) {
    phone =
      normalizeContactPhone(qPhone) ||
      normalizeContactPhone(qEmail) ||
      normalizeContactPhone(qName) ||
      "";
  }

  if (!name) {
    const nameCandidates = [qName, ...answers].filter(Boolean);
    name =
      nameCandidates.find(
        (value) =>
          !isValidContactEmail(value) &&
          !normalizeContactPhone(value) &&
          value.length >= 2
      ) || "";
  }

  for (const value of answers) {
    if (!email && isValidContactEmail(value)) {
      email = value;
      continue;
    }
    if (!phone && normalizeContactPhone(value)) {
      phone = normalizeContactPhone(value);
    }
  }

  if (!name) {
    name =
      answers.find(
        (value) =>
          value &&
          value !== email &&
          value !== phone &&
          !isValidContactEmail(value) &&
          !normalizeContactPhone(value)
      ) || "";
  }

  return {
    name: (name || `Lead Facebook ${String(lead.id).slice(-6)}`).trim(),
    email: email.trim(),
    phone: phone.trim(),
  };
};

export const buildFacebookFormContactFilters = (
  userId?: number | string,
  isPrivilegedUser = false
): GetContactsParams => {
  const filters: GetContactsParams = {
    page: 1,
    perPage: 10,
    sources: [ContactSource.FACEBOOK_FORM],
    order: "DESC",
    fieldOrder: "createdAt",
  };

  if (!isPrivilegedUser && userId) {
    filters.owner = String(userId);
  }

  return filters;
};

export const buildContactPayloadFromLead = ({
  lead,
  name,
  email,
  phone,
  formName,
  userId,
  notifyOwners = true,
}: {
  lead: FormLead;
  name: string;
  email: string;
  phone: string;
  formName: string;
  userId?: number | string;
  notifyOwners?: boolean;
}): PostContactRequest => {
  const sanitizedEmail = isValidContactEmail(email) ? email.trim() : "";
  const sanitizedPhone = normalizeContactPhone(phone);
  const formReference = `Facebook Form: ${formName || ""}`.trim();

  return {
    name: name || `Lead Facebook ${String(lead.id).slice(-6)}`,
    email: sanitizedEmail,
    source: ContactSource.FACEBOOK_FORM,
    state: 0,
    phoneNumber: sanitizedPhone,
    whatsapp: sanitizedPhone,
    reference: formReference.length >= 5 ? formReference : "Facebook Form lead",
    customFields: {},
    owners: userId ? [Number(userId)] : [],
    notifyOwners,
  };
};

export const contactHasOpportunityOnBoard = async (
  companyId: string,
  contactId: string,
  boardId: string,
  cache?: Map<string, boolean>
): Promise<boolean> => {
  const cacheKey = `${contactId}:${boardId}`;
  if (cache?.has(cacheKey)) {
    return cache.get(cacheKey)!;
  }

  const { cards } = await contactApiService.getContact(companyId, contactId);
  const hasOpportunity = (cards || []).some(
    (card) => String(card.boardId) === String(boardId)
  );

  cache?.set(cacheKey, hasOpportunity);
  return hasOpportunity;
};

export const ensureUserIsContactOwner = async (
  companyId: string,
  contactId: string,
  userId?: number | string,
  notifyOwners = true
) => {
  if (!userId) return "skipped";

  const { contact } = await contactApiService.getContact(companyId, contactId);
  const ownerIds = (contact.owners || []).map((owner: ContactOwner) =>
    Number(owner.id)
  );
  const normalizedUserId = Number(userId);

  if (ownerIds.includes(normalizedUserId)) {
    return "skipped";
  }

  await contactApiService.putContact(companyId, contactId, {
    name: contact.name,
    owners: [...ownerIds, normalizedUserId],
    notifyOwners,
  });

  return "assigned";
};
